import type { ApprovalEnvelopeRow } from '../../../../services/ApprovalEnvelopeApiClient';
import { formatIsoDateTime } from '../../../../utils/formatIsoDateTime';

export function envelopeStatusVariant(status: string): 'success' | 'info' | 'danger' | 'warning' | 'default' {
  if (status === 'Completed') return 'success';
  if (status === 'Sent' || status === 'Delivered') return 'info';
  if (status === 'Declined' || status === 'Failed') return 'danger';
  if (status === 'Created') return 'warning';
  return 'default';
}

/** Waiver resend before signature completes or after decline/failure. */
export function canResendWaiverEnvelope(envelope: ApprovalEnvelopeRow | null): boolean {
  if (!envelope) return true;
  return envelope.status === 'Created' || envelope.status === 'Declined' || envelope.status === 'Failed';
}

export type EnvelopeActivityLabel = 'Last Event' | 'Completed' | 'Sent' | 'Created';

export function envelopeActivityTimestamp(envelope: ApprovalEnvelopeRow): {
  at: string | null;
  label: EnvelopeActivityLabel;
} {
  if (envelope.lastEventAt) return { at: envelope.lastEventAt, label: 'Last Event' };
  if (envelope.completedAt) return { at: envelope.completedAt, label: 'Completed' };
  if (envelope.sentAt) return { at: envelope.sentAt, label: 'Sent' };
  if (envelope.createdAt) return { at: envelope.createdAt, label: 'Created' };
  return { at: null, label: 'Last Event' };
}

export const formatEnvelopeTimestamp = formatIsoDateTime;

export function waiverSendDisabledTitle(
  envelope: ApprovalEnvelopeRow | null,
  hasValidSignerEmail: boolean
): string | null {
  if (!hasValidSignerEmail) return 'Add a valid signer email on the Individuals step';
  if (!envelope) return null;
  switch (envelope.status) {
    case 'Completed':
      return 'Waiver already completed';
    case 'Sent':
    case 'Delivered':
      return 'Waiver is awaiting signature';
    default:
      return canResendWaiverEnvelope(envelope) ? null : `Cannot send while status is ${envelope.status}`;
  }
}

function envelopeSortTime(envelope: ApprovalEnvelopeRow): number {
  const raw = envelope.lastEventAt || envelope.sentAt || envelope.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function pickLatestEnvelope(rows: ApprovalEnvelopeRow[]): ApprovalEnvelopeRow | null {
  if (rows.length === 0) return null;
  return rows.reduce((best, row) => (envelopeSortTime(row) > envelopeSortTime(best) ? row : best));
}

export function envelopesByDocumentId(envelopes: ApprovalEnvelopeRow[]): Map<string, ApprovalEnvelopeRow> {
  const byDoc = new Map<string, ApprovalEnvelopeRow[]>();
  for (const envelope of envelopes) {
    if (envelope.status === 'Voided' || envelope.status === 'Expired') continue;
    const list = byDoc.get(envelope.analysisDocumentId) ?? [];
    list.push(envelope);
    byDoc.set(envelope.analysisDocumentId, list);
  }
  const latest = new Map<string, ApprovalEnvelopeRow>();
  for (const [documentId, list] of byDoc) {
    const row = pickLatestEnvelope(list);
    if (row) latest.set(documentId, row);
  }
  return latest;
}

export function hasInFlightWaiverEnvelopes(
  envelopes: ApprovalEnvelopeRow[],
  waiverDocumentIds: Iterable<string>
): boolean {
  const docIds = new Set(waiverDocumentIds);
  if (docIds.size === 0) return false;
  return envelopes.some((e) => docIds.has(e.analysisDocumentId) && (e.status === 'Sent' || e.status === 'Delivered'));
}
